import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  signal,
  inject,
  computed,
  ViewChild,
  viewChild,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideArrowRight, lucidePlus, lucideTrash2 } from '@ng-icons/lucide';
import { Campaign, CampaignStatus, PACKAGE_TYPE_METADATA } from '@features/campaigns/models/campaign.model';
import { CampaignsService } from '@features/campaigns/services/campaigns.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { CampaignStatusBadge } from '@features/campaigns/components/campaign-status-badge/campaign-status-badge';
import { DataTableComponent, ColumnDef, TableAction } from '@shared/components/data-table/data-table';
import { Tabs, TabItem } from '@shared/components/tabs/tabs';
import { CreateCampaignDialog } from '@features/campaigns/components/create-campaign-dialog/create-campaign-dialog';
import { CampaignStore } from '@features/campaigns/store/campaign-store';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';
import { InlineNotice } from '@shared/components/inline-notice/inline-notice';
import { StatePanel } from '@shared/components/state-panel/state-panel';

type StatusFilter = CampaignStatus | 'all';

const FILTER_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'Alle', value: 'all' },
  { label: 'Entwurf', value: CampaignStatus.DRAFT },
  { label: 'Aktiv', value: CampaignStatus.ACTIVE },
  { label: 'In Verarbeitung', value: CampaignStatus.PROCESSING },
  { label: 'Abgeschlossen', value: CampaignStatus.COMPLETED },
  { label: 'Archiviert', value: CampaignStatus.ARCHIVED },
];
@Component({
  selector: 'app-campaign-list',
  standalone: true,
  imports: [
    CommonModule,
    NgIconComponent,
    HlmButtonDirective,
    CampaignStatusBadge,
    DataTableComponent,
    Tabs,
    CreateCampaignDialog,
    InlineNotice,
    StatePanel,
  ],
  providers: [provideIcons({ lucideArrowRight, lucidePlus, lucideTrash2 })],
  templateUrl: './campaign-list.html',
  styleUrl: './campaign-list.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignList implements OnInit {
  private readonly campaignsService = inject(CampaignsService);
  readonly currentOrgStore = inject(CurrentOrganizationStore);
  private readonly campaignStore = inject(CampaignStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmationDialog = inject(ConfirmationDialogService);

  @ViewChild('createDialog') createDialog!: CreateCampaignDialog;

  // State
  campaigns = signal<Campaign[]>([]);
  isLoading = signal(false);
  error = signal<string | null>(null);
  activeStatusFilter = signal<StatusFilter>('all');

  readonly processing = CampaignStatus.PROCESSING;
  readonly canCreate = computed(() => this.currentOrgStore.can('organization', 'createcampaign'));

  /** Status tabs with counts; empty statuses stay out of the way except “Alle”. */
  readonly statusTabs = computed<TabItem[]>(() =>
    FILTER_TABS.filter((t) => t.value === 'all' || this.countByStatus(t.value) > 0 || this.activeStatusFilter() === t.value)
      .map((t) => ({ value: t.value, label: t.label, count: this.countByStatus(t.value) })),
  );

  private readonly nameTpl = viewChild.required<TemplateRef<unknown>>('nameTpl');
  private readonly statusTpl = viewChild.required<TemplateRef<unknown>>('statusTpl');
  private readonly progressTpl = viewChild.required<TemplateRef<unknown>>('progressTpl');
  private readonly countTpl = viewChild.required<TemplateRef<unknown>>('countTpl');
  private readonly dateTpl = viewChild.required<TemplateRef<unknown>>('dateTpl');

  readonly columns = computed<ColumnDef<Campaign>[]>(() => [
    { header: 'Kampagne', template: this.nameTpl(), sortValue: (c) => c.title.toLowerCase(), searchValue: (c) => `${c.title} ${c.region ?? ''}` },
    { header: 'Status', template: this.statusTpl(), sortValue: (c) => c.status, class: 'w-[150px]' },
    { header: 'Paket', cell: (c) => this.packageLabel(c), sortValue: (c) => this.packageLabel(c), class: 'w-[140px] text-dense' },
    { header: 'Fortschritt', template: this.progressTpl(), class: 'w-[200px]' },
    { header: 'Analysen', template: this.countTpl(), sortValue: (c) => c.analysisCount ?? 0, class: 'w-[110px] text-right' },
    { header: 'Erstellt', template: this.dateTpl(), sortValue: (c) => new Date(c.createdAt), class: 'w-[120px]' },
  ]);

  readonly rowActions = computed<TableAction<Campaign>[]>(() => [
    { label: 'Öffnen', icon: 'lucideArrowRight', action: (c) => this.openCampaign(c) },
    ...(this.currentOrgStore.can('campaign', 'delete')
      ? [{ label: 'Löschen', icon: 'lucideTrash2', type: 'destructive' as const, dividerBefore: true, action: (c: Campaign) => this.openDeleteDialog(c) }]
      : []),
  ]);
  readonly currentOrganization = computed(() =>
    this.currentOrgStore.organization(),
  );
  readonly orgId = computed(() => this.currentOrganization()?.id ?? '');

  /** Campaigns filtered by the active status tab */
  readonly filteredCampaigns = computed(() => {
    const filter = this.activeStatusFilter();
    if (filter === 'all') return this.campaigns();
    return this.campaigns().filter((c) => c.status === filter);
  });

  async ngOnInit(): Promise<void> {
    // Load package config in parallel with campaigns
    const orgId = this.orgId();
    if (orgId) {
      this.campaignStore.loadPackageConfig(orgId);
    }
    await this.loadCampaigns();
  }

  private async loadCampaigns(): Promise<void> {
    const organization = this.currentOrganization();
    if (!organization) return;

    this.isLoading.set(true);
    this.error.set(null);

    try {
      const campaigns = await this.campaignsService.getCampaigns(
        organization.id,
      );
      this.campaigns.set(campaigns);
    } catch (err) {
      this.error.set(
        err instanceof Error ? err.message : 'Fehler beim Laden der Kampagnen',
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  // ─── Create dialog ──────────────────────────────────────────────────────────

  openCreateDialog(): void {
    this.createDialog?.open();
  }

  onCampaignCreated(campaign: Campaign): void {
    this.campaigns.update((list) => [campaign, ...list]);
    // Navigate to the new campaign so the user sees the creation summary immediately
    this.openCampaign(campaign);
  }

  onCreateCancelled(): void {
    // Dialog closes itself via BaseCreationDialog
  }

  // ─── Navigation ─────────────────────────────────────────────────────────────

  openCampaign(campaign: Campaign): void {
    const orgSlug = this.route.snapshot.parent?.paramMap.get('slug');
    if (orgSlug) {
      this.router.navigate([
        '/organizations',
        orgSlug,
        'campaigns',
        campaign.id,
      ]);
    } else {
      this.router.navigate(['/campaigns', campaign.id]);
    }
  }

  // ─── Delete dialog ──────────────────────────────────────────────────────────

  async openDeleteDialog(campaign: Campaign): Promise<void> {
    if (!this.currentOrgStore.can('campaign', 'delete')) return;
    const organization = this.currentOrganization();
    if (!organization) return;

    const confirmed = await this.confirmationDialog.confirm({
      title: 'Kampagne löschen',
      description: `Soll „${campaign.title}“ wirklich gelöscht werden?`,
      type: 'danger',
      warningMessage: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      confirmText: 'Kampagne löschen',
    });
    if (!confirmed) return;

    this.isLoading.set(true);
    this.error.set(null);

    try {
      await this.campaignsService.deleteCampaign(organization.id, campaign.id);
      this.campaigns.update((list) => list.filter((c) => c.id !== campaign.id));
    } catch (err) {
      this.error.set(
        err instanceof Error ? err.message : 'Fehler beim Löschen der Kampagne',
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  // ─── Filter ─────────────────────────────────────────────────────────────────

  setFilter(filter: StatusFilter): void {
    this.activeStatusFilter.set(filter);
  }

  packageLabel(campaign: Campaign): string {
    const pkg = campaign.packageType;
    return pkg ? (PACKAGE_TYPE_METADATA[pkg]?.label ?? pkg) : '—';
  }

  progressPercent(campaign: Campaign): number {
    if (campaign.progress != null) return campaign.progress;
    if (campaign.targetAnalyses && campaign.completedAnalyses != null) {
      return Math.round((campaign.completedAnalyses / campaign.targetAnalyses) * 100);
    }
    return 0;
  }

  progressText(campaign: Campaign): string {
    switch (campaign.status) {
      case CampaignStatus.COMPLETED:
        return `${(campaign.completedAnalyses ?? campaign.analysisCount ?? 0).toLocaleString('de-DE')} abgeschlossen`;
      case CampaignStatus.DRAFT:
        return campaign.campaignSiId ? 'Noch nicht gestartet' : 'Wird eingerichtet';
      case CampaignStatus.ACTIVE:
        return 'Bereit für den Adressimport';
      default:
        return '—';
    }
  }

  countByStatus(status: StatusFilter): number {
    if (status === 'all') return this.campaigns().length;
    return this.campaigns().filter((c) => c.status === status).length;
  }
}
